import { useEffect, useState, useCallback} from 'react';
import { Routes, Route } from "react-router-dom";
import { MsalProvider, useMsal } from '@azure/msal-react';
import { EventType } from '@azure/msal-browser';

import { PageLayout } from './components/PageLayout';
import { TodoList } from './pages/TodoList';
import { CustomerList } from './pages/CustomerList';
import { CustomerListMui } from './pages/CustomerListMui';

import { Home } from './pages/Home';
import { b2cPolicies, protectedResources } from './authConfig';
import { compareIssuingPolicy } from './utils/claimUtils';

import './styles/App.css';
//import RedirectHandler from './RedirectHandler';

//react-notifications-component
import { ReactNotifications } from 'react-notifications-component';
import 'react-notifications-component/dist/theme.css';


//import useLogger from './hooks/useLogger'; // Adjust path as needed
//import useLoggerAdvanced from './hooks/useLoggerAdvanced';
//import useLogger2 from './hooks/useLogger2'; // Adjust path as needed
const Pages = () => {
    /**
     * useMsal is hook that returns the PublicClientApplication instance,
     * an array of all accounts currently signed in and an inProgress value
     * that tells you what msal is currently doing. For more, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
     */
    const { instance } = useMsal();

    const isAuthenticated = instance.getActiveAccount() != null;
    console.log("IsAuthenticated: " + isAuthenticated);
    
    useEffect(() => {
        const callbackId = instance.addEventCallback((event) => {
            if (
                (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
                event.payload.account
            ) {
                /**
                 * For the purpose of setting an active account for UI update, we want to consider only the auth
                 * response resulting from SUSI flow. "tfp" claim in the id token tells us the policy (NOTE: legacy
                 * policies may use "acr" instead of "tfp"). To learn more about B2C tokens, visit:
                 * https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
                 */
                if (compareIssuingPolicy(event.payload.idTokenClaims, b2cPolicies.names.editProfile)) {
                    // retrieve the account from initial sing-in to the app
                    const originalSignInAccount = instance
                        .getAllAccounts()
                        .find(
                            (account) =>
                                account.idTokenClaims.oid === event.payload.idTokenClaims.oid &&
                                account.idTokenClaims.sub === event.payload.idTokenClaims.sub && 
                                compareIssuingPolicy(account.idTokenClaims, b2cPolicies.names.signUpSignIn)        
                        );

                    let signUpSignInFlowRequest = {
                        authority: b2cPolicies.authorities.signUpSignIn.authority,
                        account: originalSignInAccount,
                    };

                    // silently login again with the signUpSignIn policy
                    instance.ssoSilent(signUpSignInFlowRequest);
                }

                /**
                 * Below we are checking if the user is returning from the reset password flow.
                 * If so, we will ask the user to reauthenticate with their new password.
                 * If you do not want this behavior and prefer your users to stay signed in instead,
                 * you can replace the code below with the same pattern used for handling the return from
                 * profile edit flow
                 */
                if (compareIssuingPolicy(event.payload.idTokenClaims, b2cPolicies.names.forgotPassword)) {
                    let signUpSignInFlowRequest = {
                        authority: b2cPolicies.authorities.signUpSignIn.authority,
                        scopes: [
                            ...protectedResources.apiTodoList.scopes.read,
                            ...protectedResources.apiTodoList.scopes.write,
                        ],
                    };
                    instance.loginRedirect(signUpSignInFlowRequest);
                }
            }

            if (event.eventType === EventType.LOGIN_FAILURE) {
                // Check for forgot password error
                // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
                if (event.error && event.error.errorMessage.includes('AADB2C90118')) {
                    const resetPasswordRequest = {
                        authority: b2cPolicies.authorities.forgotPassword.authority,
                        scopes: [],
                    };
                    instance.loginRedirect(resetPasswordRequest);
                }
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
        // eslint-disable-next-line
    }, [instance]);

    return (
        <Routes>
            <Route path="/todolist" element={<TodoList />} />
            <Route path="/customerlist" element={<CustomerList />} />
            <Route path="/customerlistmui" element={<CustomerListMui />} />
            <Route path="/" element={<Home />} />
            {/* <Route path="/redirect" element={RedirectHandler} /> */}
        </Routes>
    );
};

/**
 * msal-react is built on the React context API and all parts of your app that require authentication must be
 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication
 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the
 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const App2 = ({ instance }) => {
 
//    const {log} = useLogger(); // initialize logging hook
      //const {logAdvanced} = useLoggerAdvanced();
      // optionall, log some initial information
//    log("App2 initialized");
    //logAdvanced("App initialized");
    //const {log2} = useLogger2();
    //log2("App initialized");

    return (
        <MsalProvider instance={instance}>
            <PageLayout>
                <ReactNotifications />
                <Pages />
                {/* <AccessTokenComponent2 />  // this line cause Edit Profile to FAIL */}
            </PageLayout>
        </MsalProvider>
    );
};

//Non-memoized Version
const AccessTokenComponent = () => {
    const { instance, accounts } = useMsal();
    const [accessToken, setAccessToken] = useState(null);

    useEffect(() => {
        const acquireToken = async () => {
            if (accounts.length > 0) {
                const account = accounts[0];
                try {
                    const response = await instance.acquireTokenSilent({
                        scopes: ["https://ideacoreb2c.onmicrosoft.com/fd0129dc-ac6b-4dee-8df0-41b9f9179159/user.read"],
                        account: account
                    });
                    setAccessToken(response.accessToken);
                } catch (error) {
                    console.error("Token acquisition failed:", error);
                    // Handle token acquisition failure (e.g., fallback to interactive token request)
                }
            }
        };

        acquireToken();
    }, [instance, accounts]);

    return (
        <div>
            <h3>Access Token:</h3>
            <pre>{accessToken ? accessToken : "No token acquired"}</pre>
        </div>
    );
};

//Memoized Version
const AccessTokenComponent2 = () => {
    const { instance, accounts } = useMsal();
    const [accessToken, setAccessToken] = useState(null);

    // Memoize the acquireToken function
    const acquireToken = useCallback(async () => {
        if (accounts.length > 0) {
            const account = accounts[0];
            try {
                const response = await instance.acquireTokenSilent({
                    scopes: ["https://ideacoreb2c.onmicrosoft.com/fd0129dc-ac6b-4dee-8df0-41b9f9179159/user.read"],
                    account: account
                });
                setAccessToken(response.accessToken);
            } catch (error) {
                console.error("Token acquisition failed:", error);
                // Handle token acquisition failure (e.g., fallback to interactive token request)
            }
        }
    }, [instance, accounts]);

    useEffect(() => {
        acquireToken();
    }, [acquireToken]);

    // This will log the accessToken whenever it changes
    useEffect(() => {
        if (accessToken) {
            console.log("Token: " + accessToken);
        }
    }, [accessToken]);

    // return (
    //     <div>
    //         <h3>Access Token Memoized Version:</h3>
    //         <pre>{accessToken ? accessToken : "No token acquired"}</pre>
    //     </div>
    // );
};


export default App2;
